import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { GET_MY_PROFILE, UPDATE_QUICK_REACT_CHIPS } from '../../graphql/operations';
import { REACTION_OPTIONS } from './reactionEmojiMap';
import {
  DEFAULT_QUICK_REACT_TOKENS,
  QUICK_REACT_CHIP_COUNT,
  resolveQuickReactTokens
} from './quickReactTokens';

/**
 * KAP-310 — settings card for the per-user quick-react chip trio rendered
 * on the message hover toolbar (`MessageHoverToolbar`). Three `<select>`
 * dropdowns; Save commits `updateQuickReactChips` and writes the result
 * back into the GET_MY_PROFILE cache so subscribers refresh without a
 * refetch (mirrors the `updateNotificationSoundPreference` pattern in
 * `Sidebar.js`).
 *
 * Validation client-side mirrors the backend allowlist:
 *   - All 3 slots must be filled.
 *   - No duplicates across slots.
 *   - Every token must be in the canonical reaction-token-catalog (covered
 *     by REACTION_OPTIONS, which is the catalog's source on the frontend).
 *
 * Inline status text matches the Notifications card's transient pattern
 * (auto-clears after ~2.4s). Failure restores the prior token trio so the
 * UI doesn't lie about the persisted state.
 */
const STATUS_CLEAR_MS = 2400;

function QuickReactChipsSettings({ profile }) {
  const persisted = useMemo(
    () => resolveQuickReactTokens(profile?.quickReactChips),
    [profile?.quickReactChips]
  );

  const [draft, setDraft] = useState(persisted);
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const statusTimeoutRef = useRef(null);

  // Re-sync the editor whenever the persisted trio changes (e.g. another
  // tab edited and the subscription pushed the new profile).
  useEffect(() => {
    setDraft(persisted);
  }, [persisted]);

  useEffect(() => () => {
    if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
  }, []);

  const setTransientStatus = (next) => {
    setStatus(next);
    if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
    statusTimeoutRef.current = setTimeout(() => setStatus(''), STATUS_CLEAR_MS);
  };

  const [updateQuickReactChips] = useMutation(UPDATE_QUICK_REACT_CHIPS, {
    update: (cache, { data }) => {
      const updated = data?.updateQuickReactChips;
      if (!updated) return;
      const existing = cache.readQuery({ query: GET_MY_PROFILE });
      const current = existing?.getMyProfile || {};
      cache.writeQuery({
        query: GET_MY_PROFILE,
        data: {
          getMyProfile: {
            ...current,
            ...updated
          }
        }
      });
    }
  });

  const updateSlot = (index, token) => {
    setDraft((prev) => {
      const next = [...prev];
      next[index] = token;
      return next;
    });
  };

  const handleResetDefaults = () => {
    setDraft([...DEFAULT_QUICK_REACT_TOKENS]);
  };

  const hasDuplicate = new Set(draft).size !== draft.length;
  const allSlotsFilled = draft.every((t) => typeof t === 'string' && t.length > 0);
  const isDirty = draft.some((token, i) => token !== persisted[i]);
  const canSave = allSlotsFilled && !hasDuplicate && isDirty && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    const previous = persisted;
    try {
      await updateQuickReactChips({ variables: { chips: draft } });
      setTransientStatus('Quick reactions saved.');
    } catch (err) {
      setDraft(previous);
      setTransientStatus(err?.message || 'Failed to save quick reactions.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="popover-section popover-setting-card">
      <div className="popover-section-title">Quick reactions</div>
      <p className="popover-toggle-description">
        Three one-click reactions appear on every message&apos;s hover toolbar.
      </p>
      <div className="popover-quick-react-grid">
        {Array.from({ length: QUICK_REACT_CHIP_COUNT }, (_, slotIdx) => (
          <label key={slotIdx} className="popover-quick-react-slot">
            <span className="popover-quick-react-slot-label">{`Slot ${slotIdx + 1}`}</span>
            <select
              className="popover-quick-react-select"
              value={draft[slotIdx] || ''}
              onChange={(e) => updateSlot(slotIdx, e.target.value)}
              disabled={saving}
              aria-label={`Quick reaction slot ${slotIdx + 1}`}
            >
              {REACTION_OPTIONS.map((option) => (
                <option key={option.token} value={option.token}>
                  {option.emoji} {option.label}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
      {hasDuplicate && (
        <span className="popover-quick-react-error" role="alert">
          Each slot must pick a different reaction.
        </span>
      )}
      <div className="popover-quick-react-actions">
        <button
          type="button"
          className="popover-inline-btn popover-inline-btn-secondary"
          onClick={handleResetDefaults}
          disabled={saving}
        >
          Reset to defaults
        </button>
        <button
          type="button"
          className="popover-inline-btn"
          onClick={handleSave}
          disabled={!canSave}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      {status && (
        <span className="popover-inline-status" role="status" aria-live="polite">
          {status}
        </span>
      )}
    </div>
  );
}

export default QuickReactChipsSettings;
